import { Component, Prop, Watch } from 'vue-property-decorator'
import { ExerciseResponse } from '@src/data/exercise-response'
import { WorkoutExercise, WorkoutExerciseConfig } from '@src/data/workout-exercise'
import Vue from '@src/vue'
import WorkoutData, { HolderType } from '@src/data/workout-data'
import Club from '@src/data/club'
import { CreateElement } from 'vue'
import { Icon } from '@src/elements/icon/icon'
import { ExerciseCardConfig } from '@src/components/card/exercise-card/exercise-card-config/exercise-card-config'
import { Body1, H6, Subtitle2 } from '@src/elements/typography/typography'
import { Spacer } from '@src/elements/spacer/spacer'
import draggable from 'vuedraggable'
import { disableDragIfModalsAreOpened, DRAG_COMMON_SETTING } from '@src/util/constants'
import Station from '@src/data/station'
import { StationService } from '@src/service/station'
import Member from '@src/data/member'
import { HealthLimitationService } from '@src/service/health-limitation'
import { HealthLimitationSet } from '@src/data/health-limitation-set'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    selectedExercises: {
      get: function () {
        return this.$store.state.new_workout.workoutExercises
      },
      set: function (exercises) {
        // set is used during drag and drop only
        this.$store.state.new_workout.workoutExercises = exercises.map(exercise => {
          // when dragging a card from the ExerciseSelector, it sends an ExerciseResponse instance which needs to be converted to WorkoutExercise
          // (because the ExerciseConfiguration handles WorkoutExercise)
          if (exercise instanceof ExerciseResponse) {
            let newWorkoutExercise = new WorkoutExercise()
            newWorkoutExercise.setExercise(new ExerciseResponse(exercise))

            let initialExercise = new WorkoutExerciseConfig()
            initialExercise.setDefaultValues(new ExerciseResponse(exercise))
            newWorkoutExercise.initialExerciseConfig = initialExercise

            return newWorkoutExercise
          } else {
            // just forward the already existing list elements
            return exercise
          }
        })
      }
    },
    positionToFocusBuilderOn: {
      get: function () {
        return this.$store.state.positionToFocusBuilderOn
      },
      set: function (value) {
        this.$store.state.positionToFocusBuilderOn = value
      }
    },
    workout () {
      return this.$store.state.new_workout
    },
    club () {
      return this.$store.state.club
    },
    stations () {
      return this.$store.state.stations
    },
    member () {
      return this.$store.state.workoutMember || this.$store.state.memberProfile
    }
  }
})
export class WorkoutContent extends Vue {
  @Prop({ required: false, default: false })
  disableEdit: boolean

  protected selectedExercises: WorkoutExercise[]
  protected workout: WorkoutData
  protected club: Club
  private stations: Station[]
  private member: Member
  private healthLimitations: HealthLimitationSet = new HealthLimitationSet([])

  private positionToFocusBuilderOn: number | null

  private dragOptions: any = {
    group: {
      name: 'exerciseSelector'
    },
    ...DRAG_COMMON_SETTING,
    chosenClass: 'dragged-exercise'
  }

  created () {
    this.dragOptions.disabled = this.disableEdit
  }

  @Watch('member', { immediate: true })
  loadHeathLimitationsForMember () {
    const memberId = this.member ? this.member.id : this.workout.holderType === HolderType.MEMBER ? this.workout.holderId : null
    if (memberId) {
      return new HealthLimitationService().loadLatestHealthLimitations(memberId).then((limits) => this.healthLimitations = limits)
    } else this.healthLimitations = new HealthLimitationSet([])
  }

  mounted () {
    return new StationService().getAll().then(stations => this.$store.dispatch('setStations', stations))
  }

  updated () {
    if (this.positionToFocusBuilderOn !== null) {
      document.getElementById('exercise' + this.positionToFocusBuilderOn)!.scrollIntoView()
      this.positionToFocusBuilderOn = null
    }
  }

  render (h: CreateElement) {
    // keep an index of each exercise, to be able to identify it in the list persisted in the store; the index represents the order in the list displayed
    let index = -1
    const selectedExercises = this.selectedExercises.map(workoutExercise => {
      index++
      let stationName = this.getStationName(workoutExercise.stationId)
      const showBreakpointOption = this.workout.continuousWorkout && index !== this.selectedExercises.length - 1
      return <ExerciseCardConfig healthLimitations={this.healthLimitations} type={this.disableEdit ? 'view' : 'config'} workoutExercise={workoutExercise} club={this.club} index={index} stationName={stationName} id={'exercise' + index} showBreakpointOption={showBreakpointOption}/>
    })

    return <div slot='builder'>
      <div class={'column-header'}>
        <H6 class='title'>{this.$t('WORKOUT')}</H6>
        <span>
          <Icon class='summary-icon' name='workout/time' />
          <Subtitle2>{`${this.computeWorkoutDuration()} `}{this.$t('MINUTES')}</Subtitle2>
          <Icon class='summary-icon' name='workout/exercises' />
          <Subtitle2>{`${this.selectedExercises.length} `}{this.$t('EXERCISES')}</Subtitle2>
      </span>
      </div>
      {selectedExercises.length ?
        <draggable v-model={this.selectedExercises} class='selected-exercises' id='selectedExercises' options={this.dragOptions} onChoose={disableDragIfModalsAreOpened}>{selectedExercises}</draggable>
        :
        <draggable v-model={this.selectedExercises} options={this.dragOptions} class='empty-list'>
          <div class='empty-list-droppable'>
            <div class='empty-list-message'>
              <Icon name='workout/no-workouts-found' />
              <Spacer />
              <Body1 align='center'>{this.$t('PAGE.CONTENT.WORKOUT.NO_EXERCISES')}</Body1>
              <Spacer />
              <div class='drag-drop' draggable='false'>{this.$t('PAGE.CONTENT.WORKOUT.DRAG_DROP_EXERCISES')}</div>
            </div>
          </div>
        </draggable>
      }
    </div>
  }

  getStationName (stationId) {
    if (stationId) {
      for (let i = 0; i < this.stations.length ; i++) {
        if (this.stations[i].id === stationId) {
          if (this.stations[i].displayName) {
            return `${this.stations[i].displayName} (${ this.stations[i].name})`
          }
          return this.stations[i].name
        }
      }
    }
  }

  computeWorkoutDuration () {
    const workoutDuration = this.selectedExercises.reduce((sum, workoutExercise) => {
      sum += workoutExercise.getExerciseDuration(this.club, this.workout.type, this.workout.circleWorkout)
      return sum
    }, 0)

    return Math.ceil(workoutDuration / 60)
  }
}
